export class CmsInspectorPanel {
    #selectedElement: HTMLElement | null = null;
    #lastSelectionDetail: Record<string, unknown> = {};
    #frame = 0;

    constructor(private readonly root: HTMLElement) {}

    init(): void {
        const panel = this.root.querySelector<HTMLElement>("[data-cms-inspector-drawer]");
        if (!panel) return;
        panel.hidden = false;
        panel.setAttribute("aria-hidden", "false");
        this.root.addEventListener("cms:selection-changed", (event) => {
            const detail = (event as CustomEvent<Record<string, unknown>>).detail;
            this.#selectedElement = detail.element instanceof HTMLElement ? detail.element : null;
            this.#lastSelectionDetail = detail;
            this.updateQaPanel(detail);
            this.scheduleQaRefresh();
        });
        this.root.addEventListener("cms:inspector-opened", () => {
            this.scheduleQaRefresh();
        });
        window.addEventListener("scroll", () => this.scheduleQaRefresh(), { passive: true, capture: true });
        window.addEventListener("resize", () => this.scheduleQaRefresh(), { passive: true });
    }

    private scheduleQaRefresh(): void {
        if (this.#frame !== 0) return;
        this.#frame = window.requestAnimationFrame(() => {
            this.#frame = 0;
            this.updateQaPanel(this.#lastSelectionDetail);
        });
    }

    private updateQaPanel(detail: Record<string, unknown>): void {
        const host = this.root.querySelector<HTMLElement>("[data-cms-selection-qa]");
        if (!host) return;
        const title = this.root.querySelector<HTMLElement>("[data-cms-inspector-title]")?.textContent?.trim() ?? "Settings";
        const nodeId = typeof detail.id === "string" ? detail.id : (this.#selectedElement?.dataset.cmsEditableId ?? "");
        const nodeType = typeof detail.nodeType === "string" ? detail.nodeType : (this.#selectedElement?.dataset.cmsNodeType ?? "");
        const nodeRect = this.rect(this.#selectedElement);
        const overlayRect = this.rect(this.root.querySelector<HTMLElement>(".admin-cms-selection-overlay__box--selected:not([hidden])"));
        const drawer = this.root.querySelector<HTMLElement>("[data-cms-inspector-drawer]");
        const activeTab = this.root.querySelector<HTMLElement>("[data-cms-inspector-tab].is-active");
        const activeTabsRoot = this.root.querySelector<HTMLElement>("[data-cms-inspector-tabs]");
        const firstRow = this.root.querySelector<HTMLElement>(".admin-cms-property-row, .admin-cms-builder__right .admin-form__field");
        const firstControl = this.root.querySelector<HTMLElement>(".admin-cms-builder__right input:not([type='hidden']):not([type='checkbox']):not([type='radio']), .admin-cms-builder__right select, .admin-cms-builder__right textarea");
        this.markInspectorOverflow();
        this.setText(host, "[data-cms-qa-state]", nodeId !== "" ? "selected" : "idle");
        this.setText(host, "[data-cms-qa-node]", nodeId || "none");
        this.setText(host, "[data-cms-qa-type]", nodeType || "none");
        this.setText(host, "[data-cms-qa-node-rect]", nodeRect);
        this.setText(host, "[data-cms-qa-overlay-rect]", overlayRect);
        this.setText(host, "[data-cms-qa-inspector]", title);
        this.setText(host, "[data-cms-qa-inspector-tab]", activeTab?.dataset.cmsInspectorTab ?? "none");
        this.setText(host, "[data-cms-qa-inspector-width]", this.size(drawer, "width"));
        this.setText(host, "[data-cms-qa-row-height]", this.size(firstRow, "height"));
        this.setText(host, "[data-cms-qa-control-height]", this.size(firstControl, "height"));
        this.setText(host, "[data-cms-qa-tabs-instances]", activeTabsRoot?.dataset.inspectorTabInstanceCount ?? "none");
        this.setText(host, "[data-cms-qa-tabs-active]", activeTabsRoot?.dataset.inspectorTabActiveIndex ?? "none");
        this.setText(host, "[data-cms-qa-tabs-viewport]", activeTabsRoot?.dataset.inspectorTabViewportWidth ?? "none");
        this.setText(host, "[data-cms-qa-tabs-wrapper]", activeTabsRoot?.dataset.inspectorTabWrapperWidth ?? "none");
        this.setText(host, "[data-cms-qa-tabs-panels]", activeTabsRoot?.dataset.inspectorTabPanelWidths ?? "none");
        this.setText(host, "[data-cms-qa-tabs-overflow]", activeTabsRoot?.dataset.inspectorTabHorizontalOverflow ?? "none");
    }

    private markInspectorOverflow(): void {
        this.root.querySelectorAll<HTMLElement>(".admin-cms-builder__right .is-cms-overflowing").forEach((element) => {
            element.classList.remove("is-cms-overflowing");
        });
        this.root.querySelectorAll<HTMLElement>(".admin-cms-builder__right *").forEach((element) => {
            if (element.hidden || element.offsetParent === null) return;
            if (element.scrollWidth > element.clientWidth + 1) {
                element.classList.add("is-cms-overflowing");
            }
        });
    }

    private rect(element: HTMLElement | null): string {
        if (!element || element.hidden || !document.documentElement.contains(element)) return "none";
        const rect = element.getBoundingClientRect();
        return `x:${Math.round(rect.x)} y:${Math.round(rect.y)} w:${Math.round(rect.width)} h:${Math.round(rect.height)}`;
    }

    private size(element: HTMLElement | null, key: "width" | "height"): string {
        if (!element || element.hidden || !document.documentElement.contains(element)) return "none";
        const rect = element.getBoundingClientRect();
        return `${Math.round(rect[key])}px`;
    }

    private setText(root: HTMLElement, selector: string, value: string): void {
        const node = root.querySelector<HTMLElement>(selector);
        if (node) node.textContent = value;
    }
}
